import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { verifyEmail, resendVerificationEmail } from '../redux/AuthCRUD'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'

export function VerifyEmail() {
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState(null)
  const [resendStatus, setResendStatus] = useState(null)
  const [countdown, setCountdown] = useState(0)
  
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get user email from Redux store or from location state
  const storeEmail = useSelector(state => state.auth.user?.email || '')
  const locationEmail = location.state?.email || ''
  const email = locationEmail || storeEmail || ''
  
  // Check if there's a token in the URL (for direct verification)
  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const token = query.get('token')
    // console.log(token)
    
    if (token) {
      handleVerifyWithToken(token)
    }
  }, [location])
  
  // Handle countdown for resend button
  useEffect(() => {
    let timer
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])
  
  const handleVerifyWithToken = async (token) => {
    setVerifying(true)
    try {
      const response = await verifyEmail(token)
      setVerificationStatus({
        success: true,
        message: 'Your email has been successfully verified! You will be redirected to the login page.'
      })
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth/login')
      }, 3000)
    } catch (error) {
      setVerificationStatus({
        success: false,
        message: error.response?.data?.message || 'Verification failed. Please try again or request a new verification email.'
      })
    } finally {
      setVerifying(false)
    }
  }
  
  const handleResendEmail = async () => {
    if (countdown > 0) return
    
    setLoading(true)
    try {
      // Pass the email to the resendVerificationEmail function
      await resendVerificationEmail(email)
      setResendStatus({
        success: true,
        message: 'Verification email has been sent to your email address.'
      })
      // Set cooldown for resend button (60 seconds)
      setCountdown(60)
    } catch (error) {
      setResendStatus({
        success: false,
        message: error.response?.data?.message || 'Failed to send verification email. Please try again later.'
      })
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className='d-flex flex-column flex-root'>
      <div
        className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
        style={{
          backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/sketchy-1/14.png')})`,
        }}
      >
        <div className=''>
            <div className='text-center mb-10'>
              <h1 className='text-dark mb-3'>Email Verification</h1>
              
              {verifying ? (
                <div className='fs-3 fw-bold text-muted mb-10'>Verifying your email...</div>
              ) : verificationStatus ? (
                <div className={`alert ${verificationStatus.success ? 'alert-success' : 'alert-danger'}`}>
                  {verificationStatus.message}
                </div>
              ) : (
                <>
                  <div className='fs-3 fw-bold text-muted mb-10'>
                    We have sent a verification email to:
                    <br />
                    <span className='fw-bolder text-dark'>{email}</span>
                  </div>
                  
                  <div className='fs-6 fw-bold text-muted mb-10'>
                    Please check your email and click on the verification link to activate your account.
                    <br />
                    If you don't see the email, check your spam folder.
                  </div>
                  
                  {resendStatus && (
                    <div className={`alert ${resendStatus.success ? 'alert-success' : 'alert-danger'} mb-10`}>
                      {resendStatus.message}
                    </div>
                  )}
                  
                  <button
                    type='button'
                    className='btn btn-lg btn-primary w-100 mb-5'
                    onClick={handleResendEmail}
                    disabled={loading || countdown > 0}
                  >
                    {loading ? (
                      <span className='indicator-progress' style={{ display: 'block' }}>
                        Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) : countdown > 0 ? (
                      `Resend Email (${countdown}s)`
                    ) : (
                      'Resend Verification Email'
                    )}
                  </button>
                  
                  <button
                    type='button'
                    className='btn btn-lg btn-light w-100'
                    onClick={() => navigate('/auth/login')}
                  >
                    Go to Login
                  </button>
                </>
              )}
            </div>
          </div>
      </div>
    </div>
  )
}